/**
 * Label version draft (F7.4). uc-core CreateLabelVersionDto / UpdateLabelVersionDto: skuId, code
 * ≤ 20, premisesLocationId (a PACKING hall), mrpInr "180" / "180.00", allergenStatement ≤ 200,
 * claimText ≤ 300, claimPanel ≤ 40 (stored upper case), claimMinAnalytes ≥ 1, claimMaxLoq ≤ 4
 * decimals, artworkUrl ≤ 1000. A draft may be saved incomplete; approval needs every line
 * (labelApprovalProblems in src/modules/pack/utils/pack-rules.util.ts — mirrored here in plain words).
 */

export interface LabelDraft {
  skuId: string
  code: string
  premisesLocationId: string
  mrpInr: string
  allergenStatement: string
  claimText: string
  claimPanel: string
  claimMinAnalytes: string
  claimMaxLoq: string
  artworkUrl: string
}

export type LabelProblems = Partial<Record<keyof LabelDraft, string>>

export const emptyLabelDraft = (): LabelDraft => ({
  skuId: '',
  code: '',
  premisesLocationId: '',
  mrpInr: '',
  allergenStatement: '',
  claimText: '',
  claimPanel: '',
  claimMinAnalytes: '',
  claimMaxLoq: '',
  artworkUrl: '',
})

export interface LabelLike {
  skuId: string
  code: string
  premisesLocationId: string
  mrpInr: string | null
  allergenStatement: string | null
  claimText: string | null
  claimPanel: string | null
  claimMinAnalytes: number | null
  claimMaxLoq: string | null
  artworkUrl: string | null
}

export function draftFromLabel(l: LabelLike): LabelDraft {
  return {
    skuId: l.skuId,
    code: l.code,
    premisesLocationId: l.premisesLocationId,
    mrpInr: l.mrpInr ?? '',
    allergenStatement: l.allergenStatement ?? '',
    claimText: l.claimText ?? '',
    claimPanel: l.claimPanel ?? '',
    claimMinAnalytes: l.claimMinAnalytes === null ? '' : String(l.claimMinAnalytes),
    claimMaxLoq: l.claimMaxLoq ?? '',
    artworkUrl: l.artworkUrl ?? '',
  }
}

/** A new version of an existing label: same lines, a fresh code to type. */
export function draftCopyOf(l: LabelLike): LabelDraft {
  return { ...draftFromLabel(l), code: nextVersionCode(l.code) }
}

/** "L-GN500-v4" → "L-GN500-v5"; anything else gets "-v2". */
export function nextVersionCode(code: string): string {
  const m = /^(.*-v)(\d+)$/i.exec(code)
  const next = m ? `${m[1]}${Number(m[2]) + 1}` : `${code}-v2`
  return next.slice(0, 20)
}

const MRP = /^\d+(\.\d{1,2})?$/
const DEC4 = /^\d+(\.\d{1,4})?$/

// ---- claim words ------------------------------------------------------------

const FORBIDDEN: Array<{ re: RegExp; word: string }> = [
  { re: /residue[\s-]*free/i, word: 'residue-free' },
  { re: /pesticide[\s-]*free/i, word: 'pesticide-free' },
  { re: /\bnil\b/i, word: 'nil' },
]

/** The words in a claim the law and our rules do not allow (same patterns as the server). */
export function forbiddenClaimWords(text: string | null | undefined): string[] {
  if (!text) return []
  return FORBIDDEN.filter((f) => f.re.test(text)).map((f) => f.word)
}

/** One sentence about the forbidden words, or null. */
export function claimWordsProblem(text: string | null | undefined): string | null {
  const words = forbiddenClaimWords(text)
  if (words.length === 0) return null
  const list = words.map((w) => `“${w}”`).join(', ')
  return `The claim must not say ${list}. Write what was tested and that it was not detected, e.g. “Tested for 58 pesticides: not detected”.`
}

// ---- save -------------------------------------------------------------------

export function validateLabelDraft(d: LabelDraft, mode: 'create' | 'edit'): LabelProblems {
  const p: LabelProblems = {}
  if (mode === 'create') {
    if (!d.skuId) p.skuId = 'Pick the product'
    const code = d.code.trim()
    if (!code) p.code = 'Enter the version code printed on the roll'
    else if (code.length > 20) p.code = 'At most 20 characters'
  }
  if (!d.premisesLocationId) p.premisesLocationId = 'Pick the packing hall'
  const mrp = d.mrpInr.trim()
  if (!mrp) p.mrpInr = 'Enter the MRP'
  else if (!MRP.test(mrp)) p.mrpInr = 'Rupees with at most 2 decimals, e.g. 180 or 180.00'
  else if (Number(mrp) <= 0) p.mrpInr = 'The MRP must be above ₹0'
  if (d.allergenStatement.trim().length > 200) p.allergenStatement = 'At most 200 characters'
  if (d.claimText.trim().length > 300) p.claimText = 'At most 300 characters'
  else {
    const w = claimWordsProblem(d.claimText)
    if (w) p.claimText = w
  }
  if (d.claimPanel.trim().length > 40) p.claimPanel = 'At most 40 characters'
  const n = d.claimMinAnalytes.trim()
  if (n && (!/^\d{1,5}$/.test(n) || Number(n) < 1)) p.claimMinAnalytes = 'A whole number, e.g. 58'
  const loq = d.claimMaxLoq.trim()
  if (loq && !DEC4.test(loq)) p.claimMaxLoq = 'A number with at most 4 decimals, e.g. 0.01'
  if (d.artworkUrl.trim().length > 1000) p.artworkUrl = 'Too long'
  return p
}

export const labelOk = (p: LabelProblems) => Object.values(p).every((v) => !v)

const opt = (s: string) => s.trim()

export function createLabelBody(d: LabelDraft) {
  return {
    skuId: d.skuId,
    code: d.code.trim(),
    premisesLocationId: d.premisesLocationId,
    mrpInr: d.mrpInr.trim(),
    ...(opt(d.allergenStatement) ? { allergenStatement: opt(d.allergenStatement) } : {}),
    ...(opt(d.claimText) ? { claimText: opt(d.claimText) } : {}),
    ...(opt(d.claimPanel) ? { claimPanel: opt(d.claimPanel).toUpperCase() } : {}),
    ...(opt(d.claimMinAnalytes) ? { claimMinAnalytes: Number(opt(d.claimMinAnalytes)) } : {}),
    ...(opt(d.claimMaxLoq) ? { claimMaxLoq: opt(d.claimMaxLoq) } : {}),
    ...(opt(d.artworkUrl) ? { artworkUrl: opt(d.artworkUrl) } : {}),
  }
}

/** PATCH body: text lines are always sent (an emptied line is cleared); numbers only when typed. */
export function updateLabelBody(d: LabelDraft) {
  return {
    premisesLocationId: d.premisesLocationId,
    mrpInr: d.mrpInr.trim(),
    allergenStatement: opt(d.allergenStatement),
    claimText: opt(d.claimText),
    claimPanel: opt(d.claimPanel).toUpperCase(),
    ...(opt(d.claimMinAnalytes) ? { claimMinAnalytes: Number(opt(d.claimMinAnalytes)) } : {}),
    ...(opt(d.claimMaxLoq) ? { claimMaxLoq: opt(d.claimMaxLoq) } : {}),
    ...(opt(d.artworkUrl) ? { artworkUrl: opt(d.artworkUrl) } : {}),
  }
}

// ---- approval ---------------------------------------------------------------

/**
 * What stops approval, in plain words — the same checks the server runs, so the quality person
 * sees them before pressing Approve.
 */
export function approvalProblems(l: {
  mrpInr: string | null
  allergenStatement: string | null
  claimText: string | null
  claimPanel: string | null
  claimMinAnalytes: number | null
  claimMaxLoq: string | null
  hallLicenceNo: string | null
}): string[] {
  const p: string[] = []
  if (l.mrpInr === null || l.mrpInr === '') p.push('The MRP is missing.')
  if (!l.allergenStatement?.trim()) p.push('The allergen line is missing.')
  if (!l.claimText?.trim()) p.push('The claim text is missing.')
  if (!l.claimPanel?.trim()) p.push('The test panel behind the claim is missing.')
  if (!l.claimMinAnalytes) p.push('The number of substances tested is missing.')
  if (l.claimMaxLoq === null || l.claimMaxLoq === '') p.push('The highest detection limit allowed is missing.')
  if (!l.hallLicenceNo) p.push('The packing hall has no FSSAI licence on file.')
  const w = claimWordsProblem(l.claimText)
  if (w) p.push(w)
  return p
}

/** The server's problem lines ("mrp_inr is missing") in plain words. */
export function readableApprovalProblem(line: string): string {
  const known: Array<[RegExp, string]> = [
    [/^mrp_inr is missing/, 'The MRP is missing.'],
    [/^allergen_statement is missing/, 'The allergen line is missing.'],
    [/^claim_text is missing/, 'The claim text is missing.'],
    [/^claim_panel is missing/, 'The test panel behind the claim is missing.'],
    [/^claim_min_analytes is missing/, 'The number of substances tested is missing.'],
    [/^claim_max_loq is missing/, 'The highest detection limit allowed is missing.'],
    [/no FSSAI licence/i, 'The packing hall has no FSSAI licence on file.'],
    [/must not say/, "The claim must not say “residue-free”, “pesticide-free” or “nil”; write “tested, not detected”."],
  ]
  for (const [re, text] of known) if (re.test(line)) return text
  return line.replace(/_/g, ' ')
}

// ---- preview ----------------------------------------------------------------

/** "180 days" → the best-before line printed on the pouch. */
export function bestBeforeLine(shelfLifeDays: number | null): string {
  if (!shelfLifeDays) return 'Best before: shelf life not set'
  if (shelfLifeDays % 30 === 0 && shelfLifeDays >= 30) {
    const m = shelfLifeDays / 30
    return `Best before ${m} ${m === 1 ? 'month' : 'months'} from packing (${shelfLifeDays} days)`
  }
  return `Best before ${shelfLifeDays} ${shelfLifeDays === 1 ? 'day' : 'days'} from packing`
}

/** The shape of a pack lot code as printed: PL-YYYY-MMDD-NNNN-K for a date. */
export function sampleLotCode(date: string): string {
  const [y = '2026', m = '01', d = '01'] = date.split('-')
  return `PL-${y}-${m}${d}-0001-K`
}

/** Net quantity as printed: 500 g, 1 kg, 1.5 kg. */
export function netQuantityLine(netG: number): string {
  if (netG >= 1000) {
    const kg = netG / 1000
    return `${Number.isInteger(kg) ? kg : kg.toFixed(3).replace(/0+$/, '')} kg`
  }
  return `${netG} g`
}
